import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { ConsentPreferencesCookie } from '../core'
import type { ConsentCategory, ConsentCookieOptions } from '../core/constants'
import { missingProviderProxy } from './util'

/** A React context for using a {@link ConsentPreferencesCookie} */
export interface ConsentPreferencesContextValue
  extends Pick<
    ConsentPreferencesCookie,
    'allowed' | 'acknowledged' | 'options'
  > {
  /** Sets the consent for a specific category */
  setConsent(category: ConsentCategory, allowed: boolean): void

  /** Sets the exact allowed consents */
  allowOnly(allowed: Iterable<ConsentCategory>): void

  /** Allows all consent categories */
  allowAll(): void

  /** Rejects all consent categories (except for 'required') */
  rejectAll(): void
}

/**
 * @internal Do not use this directly, use `ConsentPreferencesProvider` and
 * `useConsentPreferences` instead.
 */
export const ConsentPreferencesContext = createContext(
  missingProviderProxy(
    {} as ConsentPreferencesContextValue,
    'ConsentPreferences',
  ),
)

function defaultInitialCookie() {
  if (typeof document !== 'undefined') {
    return document.cookie
  }
  return null
}

export type ConsentPreferencesProviderProps = PropsWithChildren<{
  /**
   * A function which provides the initial cookie value.
   *
   * - On the client, you can use `() => document.cookie`.
   * - On the server, you can use the `Cookie` header from the current request.
   */
  initialCookie?: () => string | null

  /** The options for reading/writing the cookie */
  cookieOptions?: ConsentCookieOptions

  /**
   * If true (default), then changes to consent preferences are automatically
   * saved to the document cookie.
   */
  persistCookieToDocument?: boolean
}>

/**
 * Manages consent preferences for your React application.
 *
 * @example
 * ```tsx
 * import { ConsentPreferencesProvider } from '@mondough/consent-management/react'
 *
 * function App() {
 *   return (
 *     <ConsentPreferencesProvider initialCookie={() => document.cookie}>
 *       <YourApp />
 *     </ConsentPreferencesProvider>
 *   )
 * }
 * ```
 */
export const ConsentPreferencesProvider = ({
  children,
  initialCookie = defaultInitialCookie,
  cookieOptions,
  persistCookieToDocument = true,
}: ConsentPreferencesProviderProps) => {
  // We store consent preferences in a state variable
  // TODO: Should this be initialised via useEffect, to prevent SSR hydration errors?
  // Do we ever actually care about rendering cookie consent server-side?
  const [consents, setConsents] = useState(() =>
    ConsentPreferencesCookie.from(initialCookie?.(), cookieOptions),
  )

  // If consent preferences change, save them to the document cookie
  useEffect(() => {
    if (persistCookieToDocument) consents.saveToDocumentCookie()
  }, [consents, persistCookieToDocument])

  // Build a memoised wrapper around the consent preferences object
  const context = useMemo<ConsentPreferencesContextValue>(
    () => ({
      allowed: consents.allowed,
      acknowledged: consents.acknowledged,
      options: consents.options,
      setConsent: (category, allowed) =>
        setConsents(consents.withConsent(category, allowed)),
      allowOnly: (allowed) => setConsents(consents.allowingOnly(allowed)),
      allowAll: () => setConsents(consents.allowingAll()),
      rejectAll: () => setConsents(consents.rejectingAll()),
    }),
    [consents],
  )

  return (
    <ConsentPreferencesContext.Provider value={context}>
      {children}
    </ConsentPreferencesContext.Provider>
  )
}

/**
 * Allows access to the consent preferences context.
 *
 * @see {@link ConsentPreferencesContextValue}
 */
export const useConsentPreferences = () => useContext(ConsentPreferencesContext)
